'use client'

import React, { useEffect, useState, useRef } from 'react'
import { motion, AnimatePresence } from 'framer-motion'
import { NavigationBar } from './NavigationBar'
import MobileBottomNav from './MobileBottomNav'
import { OverviewSection } from '../OverviewSection'
import { FormulationSection } from '../FormulationSection'
import { PartnershipSection } from '../PartnershipSection'
import { GridBackground } from './GridBackground'
import { ContactUsModal } from './ContactUsModal'
import { Button } from '@/components/ui/button'
import { useReducedMotion } from '@/lib/hooks/useReducedMotion'
import Image from 'next/image'
import Link from 'next/link'
import { useSearchParams } from 'next/navigation'

export function HorizontalScroll() {
  const searchParams = useSearchParams()
  const initialTab = searchParams.get('tab')

  // Map tab names to section indices
  const getInitialSection = () => {
    if (initialTab === 'formulation' || initialTab === 'chemist') return 1
    if (initialTab === 'partnership' || initialTab === 'partner') return 2
    return 0 // default to overview/ingredients
  }

  const [activeSection, setActiveSection] = useState(getInitialSection())
  const scrollRef = useRef<HTMLDivElement>(null)
  const reducedMotion = useReducedMotion()
  const hasScrolledToInitial = useRef(false)
  
  const sections = [
    {
      id: 'overview',
      component: <OverviewSection />
    },
    {
      id: 'formulation',
      component: <FormulationSection />
    },
    {
      id: 'partnership',
      component: <PartnershipSection />
    }
  ]
  
  // Initial scroll to section based on URL parameter
  useEffect(() => {
    if (!scrollRef.current || hasScrolledToInitial.current) return

    const initialSection = getInitialSection()
    if (initialSection > 0) {
      // Small delay to ensure DOM is ready
      setTimeout(() => {
        if (!scrollRef.current) return
        const containerWidth = scrollRef.current.clientWidth
        scrollRef.current.scrollTo({
          left: containerWidth * initialSection,
          behavior: 'auto' // instant scroll on initial load
        })
        hasScrolledToInitial.current = true
      }, 100)
    } else {
      hasScrolledToInitial.current = true
    }
  }, [])

  // Handle scroll events to update active section
  useEffect(() => {
    const handleScroll = () => {
      if (!scrollRef.current) return
      const scrollPosition = scrollRef.current.scrollLeft
      const containerWidth = scrollRef.current.clientWidth
      const newActiveSection = Math.round(scrollPosition / containerWidth)
      if (newActiveSection !== activeSection) {
        setActiveSection(newActiveSection)
      }
    }

    const scrollContainer = scrollRef.current
    if (scrollContainer) {
      scrollContainer.addEventListener('scroll', handleScroll)
    }

    return () => {
      if (scrollContainer) {
        scrollContainer.removeEventListener('scroll', handleScroll)
      }
    }
  }, [activeSection])
  
  // Handle navigation click
  const handleNavigate = (index: number) => {
    // Check if clicking on Partner Portal (index 2) and user has auth token
    if (index === 2) {
      // Check for NextAuth session token
      const hasSession = document.cookie.includes('next-auth.session-token') || 
                         document.cookie.includes('__Secure-next-auth.session-token')
      
      if (hasSession) {
        // User is logged in, redirect directly to portal
        window.location.href = '/portal'
        return
      }
    }
    
    if (!scrollRef.current) return
    const containerWidth = scrollRef.current.clientWidth
    scrollRef.current.scrollTo({
      left: containerWidth * index,
      behavior: 'smooth'
    })
    setActiveSection(index)
  }

  // Handle mobile navigation
  const handleMobileNavigate = (section: 'ingredients' | 'chemist' | 'partner') => {
    const sectionMap = {
      'ingredients': 0,
      'chemist': 1,
      'partner': 2
    }
    handleNavigate(sectionMap[section])
  }

  // Get active section name for mobile nav
  const getActiveSectionName = () => {
    const names = ['ingredients', 'chemist', 'partner']
    return names[activeSection]
  }
  
  // ✅ OPTIMIZED: Page transition variants with reduced motion support
  const pageVariants = {
    initial: (direction: number) => ({
      opacity: 0,
      x: reducedMotion ? 0 : (direction > 0 ? 100 : -100),
      scale: reducedMotion ? 1 : 0.95,
      willChange: reducedMotion ? 'auto' : 'transform, opacity'
    }),
    animate: {
      opacity: 1,
      x: 0,
      scale: 1,
      willChange: reducedMotion ? 'auto' : 'transform, opacity',
      transition: {
        duration: reducedMotion ? 0 : 0.5,
        type: reducedMotion ? 'tween' as const : 'spring' as const,
        stiffness: 100,
        damping: 20
      }
    },
    exit: (direction: number) => ({
      opacity: 0,
      x: reducedMotion ? 0 : (direction > 0 ? -100 : 100),
      scale: reducedMotion ? 1 : 0.95,
      willChange: 'auto',
      transition: {
        duration: reducedMotion ? 0 : 0.3
      }
    })
  }
  
  return (
    <div className="relative w-full min-h-screen bg-brand-surface-dark">
      {/* Fixed wrapper for Grid Background */}
      <div className="fixed inset-0 z-0 pointer-events-none">
        <GridBackground />
      </div>
      
      {/* Sticky Navigation Row - logo + hero nav (visible on tablets and desktop) */}
      <div className="sticky top-0 left-0 right-0 z-[60] hidden sm:flex justify-center h-16 items-center bg-transparent">
        <div className="flex items-center gap-8 mt-10 px-6">
          <Link href="/" className="flex-shrink-0 transition-transform duration-300 hover:scale-105 p-2 rounded-xl animate-flicker-in">
            <Image
              src="/images/eskerium-logo.png"
              alt="Eskerium Logo"
              width={160}
              height={52}
              className="h-auto"
              priority
            />
          </Link>
          <NavigationBar
            sections={sections.map(s => s.id)}
            activeSection={activeSection}
            onNavigate={handleNavigate}
            actionButton={
              <ContactUsModal>
                <Button
                  variant="outline"
                  className="rounded-full border-white/20 hover:border-white/30 text-white/80 hover:text-white text-sm"
                  size="sm"
                >
                  Contact Us
                </Button>
              </ContactUsModal>
            }
          />
        </div>
      </div>
      
      {/* Mobile Header - logo only (phones only) */}
      <div className="sticky top-0 left-0 right-0 z-[60] flex sm:hidden justify-center h-16 items-center bg-transparent">
        <Link href="/" className="flex-shrink-0 transition-transform duration-300 hover:scale-105 p-2 rounded-xl animate-flicker-in">
          <Image
            src="/images/eskerium-logo.png"
            alt="Eskerium Logo"
            width={140}
            height={46}
            className="h-auto"
            priority
          />
        </Link>
      </div>
      
      {/* Horizontal Scrolling Container - relative positioning for natural height */}
      <div
        ref={scrollRef}
        className="relative w-full min-h-screen pt-0 flex overflow-x-auto overflow-y-visible snap-x snap-mandatory hide-scrollbar"
        style={{
          scrollbarWidth: 'none',
          msOverflowStyle: 'none',
          WebkitOverflowScrolling: 'touch'
        }}
      >
        {/* ✅ OPTIMIZED: Only render active section to reduce DOM nodes and improve performance */}
        {sections.map((section, index) => (
          <div
            key={section.id}
            className="min-h-screen min-w-full flex items-start justify-center snap-center pt-6 pb-10"
          >
            {activeSection === index && (
              <AnimatePresence mode="wait">
                <motion.div
                  className="w-full flex items-start pt-2"
                  custom={activeSection - index}
                  variants={pageVariants}
                  initial="initial"
                  animate="animate"
                  exit="exit"
                  key={section.id}
                >
                  <div className="w-full">
                    {section.component}
                  </div>
                </motion.div>
              </AnimatePresence>
            )}
          </div>
        ))}
      </div>
      
      {/* Mobile Bottom Navigation */}
      <MobileBottomNav 
        onNavigate={handleMobileNavigate}
        activeSection={getActiveSectionName()}
      />
    </div>
  )
}